import {
  AuditType,
  AuditViewType,
  CreateAuditBatchBody,
  CreateAuditBatchResponse,
} from '@scale/shared/types/corp/attemptAudit';
import _ from 'lodash';
import { useMutation } from 'react-query';
import {
  createBatchNameForProjectId,
  createBatchNameForTaskId,
} from 'react-scale/components/ExpertManagement/helpers/audit';
import {
  AuditDto,
  useAuditActions,
} from 'react-scale/components/ExpertManagement/stores/audit.store';
import apiFetch, { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';

type CreateAuditBatchForTaskIdsParams = {
  customAuditUrl?: (auditBatchId: string) => string;
};

export const useCreateAuditBatchForTaskIds = (
  { customAuditUrl }: CreateAuditBatchForTaskIdsParams = {
    customAuditUrl: undefined,
  },
) => {
  const setLoadedAudit = useAuditActions().setLoadedAudit;
  return useMutation<
    AuditDto | undefined,
    Error,
    { projectId: string; taskIds: string[]; chatBulkAudit?: boolean; auditGoal?: string }
  >('useCreateAuditBatchForTaskId', async ({ projectId, taskIds, chatBulkAudit, auditGoal }) => {
    try {
      if (taskIds.length === 0) {
        return undefined;
      }

      const auditName =
        taskIds.length === 1
          ? createBatchNameForTaskId(taskIds[0])
          : createBatchNameForProjectId(projectId);

      if (chatBulkAudit === true) {
        const { auditBatch, attemptAudits, projectInstrItems, erroredTasks } =
          await apiFetch<CreateAuditBatchResponse>(null, `corp-api/chatBulkAudit/batch`, {
            method: 'POST',
            body: {
              projectId,
              name: auditName,
              objectIds: taskIds,
              auditGoal,
            },
          });

        if (!_.isEmpty(erroredTasks)) {
          alert(
            `The following tasks were not able to be added to the audit. This might be because the task wasn't found, it doesn't have any subtasks or attempts, or the last response is blank and there is nothing to audit.
          List of errored tasks: ${erroredTasks.join(', ')}`,
          );
        }

        const auditDto = {
          auditBatch,
          attemptAudits,
          projectInstrItems,
          attemptIds: attemptAudits.map(attemptAudit => attemptAudit.auditLookup.id),
          projectId,
          auditGoal,
        };

        const auditUrl = new URL(window.location.origin);
        auditUrl.pathname = customAuditUrl
          ? customAuditUrl(auditBatch._id)
          : `corp/chat_bulk_audit/${auditBatch._id}`;
        window.open(auditUrl, '_blank');

        return auditDto;
      } else {
        const body: CreateAuditBatchBody = {
          projectId,
          name: auditName,
          objectIds: taskIds,
          assignmentParams: { userIds: [], workerTeamIds: [] },
          type: AuditType.DEFAULT,
          auditViewType: AuditViewType.STANDARD,
          claimTasks: false,
          auditGoal,
        };

        const { auditBatch, attemptAudits, projectInstrItems, erroredTasks } =
          await apiFetch<CreateAuditBatchResponse>(null, `corp-api/attemptAudit/auditBatches`, {
            method: 'POST',
            body,
          } as any);

        if (!_.isEmpty(erroredTasks)) {
          alert(
            `The following tasks were not able to be added to the audit. This might be because the task wasn't found, it doesn't have any subtasks or attempts, or the last response is blank and there is nothing to audit.
          List of errored tasks: ${erroredTasks.join(', ')}`,
          );
        }

        const attemptIds = attemptAudits.map(attemptAudit => attemptAudit.auditLookup.id);

        const auditDto = { auditBatch, attemptAudits, projectInstrItems, attemptIds, projectId };

        setLoadedAudit(auditDto);

        return auditDto;
      }
    } catch (e) {
      defaultApiFetchErrorHandler(e, { handleHttp: true });
      return;
    }
  });
};
